import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { fullYearFormat, NEXT, pageSizeOptions } from 'src/app/core/constants/configuration/common.constant';
import { LoaderMessagingService } from 'src/app/core/services/messaging/loader-messaging.service';
import { CrudService } from 'src/app/core/services/data/crud.service';
import { ErrorHandlerService } from 'src/app/core/services/util/error-handler.service';
import { allStudentsEndpoint, allGradeLevelsEndpoint, getAllSchoolYearsEndpoint, getActiveSchoolYearEndpoint, checkIfWithinPreOrderingWindow } from 'src/app/core/constants/endpoints.constant';
import { QueryService } from 'src/app/core/services/util/query.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { DataCustomizerService } from 'src/app/core/services/util/data-customizer.service';
import { DataProperties } from 'src/app/core/models/data-properties.model';
import { Student } from 'src/app/core/models/eos/student.model';
import { studentDataProperties } from 'src/app/core/constants/configuration/order-constants.config';
import { UserService } from 'src/app/core/services/util/user.service';
import { TEACHER } from 'src/app/core/constants/configuration/role-constants.config';
import * as moment from 'moment';
import { MatDialog } from '@angular/material';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { ADMIN } from 'src/app/core/constants/configuration/role-constants.config';

@Component({
  selector: 'app-order-student-selection',
  templateUrl: './order-student-selection.component.html',
  styleUrls: ['./order-student-selection.component.scss']
})
export class OrderStudentSelectionComponent implements OnInit {

  @Output() getFormValue = new EventEmitter<any>();
  @Output() canProceedWithSelectedSy = new EventEmitter<any>();
  items: any[] = [];
  itemsCount = this.items.length;
  pageIndex: number = 0;
  pageSize: number = pageSizeOptions[0];
  keyword: string = "";
  filters: DataProperties[];
  selectedFilter: string;
  unsubscribe: Subject<any> = new Subject();
  reference: any = {};

  selectedStudentId: number;
  students: any;

  clearFilters: boolean = true;
  studentDataProperties: DataProperties[] = studentDataProperties;
  filterLabels: string[] = [];
  includeInactive: boolean = false;

  schoolYears: any[] = [];
  selectedSyId: number;
  schoolYear: string;
  currentSchoolYear: string;
  currentSyId: number;
  isWithinActivePreOrderingWindow: boolean = true;

  constructor(
    private loaderMessagingService: LoaderMessagingService,
    private crudService: CrudService,
    private errorHandlerService: ErrorHandlerService,
    private queryService: QueryService,
    private dataCustomizerService: DataCustomizerService,
    private userService: UserService,
    private dialog: MatDialog,
  ) {
    this.loaderMessagingService.showPageLoader(true);
    this.includeInactive = !this.userService.hasRole([TEACHER]);
  }

  ngOnInit() {
    this.selectedStudentId = null;
    this.filters = Object.assign([], this.studentDataProperties.filter(prop => prop.filterable));
    this.selectedFilter = this.filters[1].property;
    this.setReferenceGradeLevels();
    this.getSchoolYears();
    this.checkIfWithinPreOrderingWindow();
  }

  initParams(): void {
    this.setSchoolYear(null);
  }

  getSchoolYears(): void {
    this.crudService
      .getById<any>(getAllSchoolYearsEndpoint)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
        this.schoolYears = response;
        this.schoolYears.sort((a, b) => b.schoolYear.localeCompare(a.schoolYear));
        this.initParams();
      }, this.errorHandlerService.handleError);
  }

  setSchoolYear(schoolYear: any): void {
    if(schoolYear != undefined && schoolYear != null) {
        this.selectedSyId = schoolYear.id;
        this.schoolYear = schoolYear.schoolYear;
        this.search();
        this.canProceedWithSelectedSy.emit({ canProceed: this.canProceed, schoolYearCode: this.schoolYear, teachersCanOrder: this.teachersCanOrderForTheCurrentSy });
    } else {
      this.crudService
      .getById<any>(getActiveSchoolYearEndpoint)
      .pipe(takeUntil(this.unsubscribe))
			.subscribe(response => {
				if (response) {
          this.currentSchoolYear = response.schoolYear;
          this.currentSyId = response.id;
		      this.schoolYear = response.schoolYear;
          this.selectedSyId = this.schoolYears.find(sy =>
            String(sy.schoolYear).trim() === this.schoolYear.trim()).id;
          this.canProceedWithSelectedSy.emit({ canProceed: this.canProceed, schoolYearCode: this.schoolYear, teachersCanOrder: this.teachersCanOrderForTheCurrentSy, isCurrentSY: this.currentSchoolYear == this.schoolYear });
				}
    	}, this.errorHandlerService.handleError);
    }
  }

  get canProceed() {
    if (this.teachersCanOrderForTheCurrentSy) {
      return (this.userService.hasRole([ADMIN]) && this.selectedSyId >= this.currentSyId) || this.currentSchoolYear == this.schoolYear || (this.currentSchoolYear != this.schoolYear && this.isWithinActivePreOrderingWindow && this.selectedSyId >= this.currentSyId);
    } else {
      return (this.userService.hasRole([ADMIN]) && this.selectedSyId >= this.currentSyId) || (this.currentSchoolYear == this.schoolYear && !this.userService.hasRole([TEACHER])) || (this.currentSchoolYear != this.schoolYear && this.isWithinActivePreOrderingWindow && this.selectedSyId >= this.currentSyId);
    }
  }

  get teachersCanOrderForTheCurrentSy() {
    return this.userService.hasRole([TEACHER])? false : true; // SG_CR_266 uncomment this if ordering is disabled for teachers
    // return true; // SG_CR_266 uncomment this if ordering is enabled for teachers
  }

  checkIfWithinPreOrderingWindow() {
    this.crudService
      .getById<any>(checkIfWithinPreOrderingWindow)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
        this.isWithinActivePreOrderingWindow = response;
      }, this.errorHandlerService.handleError);
  }

  search() {
    this.loaderMessagingService.showListLoader(true);
    this.items = [];

    this.crudService
      .getAll<Student>(allStudentsEndpoint.concat(this.queryService.buildStudentSearchQuery(
        this.pageIndex, this.pageSize, this.includeInactive, this.keyword, this.selectedFilter, this.schoolYear)))
      .pipe(takeUntil
      (this.unsubscribe))
      .subscribe(response => {
        if (response) {
          this.itemsCount = response.totalElements;
          response.content.forEach((student: Student) =>
            this.items.push(this.dataCustomizerService.formatStudentDetailDisplay(student, this.reference)));
        }

      },
        this.errorHandlerService.handleError,
        this.handleCompletion
    );

  }

  private setReferenceGradeLevels(): void {

		this.crudService
			.getById<any>(allGradeLevelsEndpoint)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(response => {

				if (response) {
					this.reference.gradeLevels = response;
					this.reference.gradeLevels.sort((a, b) => a.code.localeCompare(b.code));
					this.search();
				}

			}, this.errorHandlerService.handleError);

  }
  
  handleCompletion = (): void => {
    this.loaderMessagingService.showListLoader(false);
    this.loaderMessagingService.showPageLoader(false);
  };

  onSearch(keyword: string): void {
		this.keyword = keyword;
		this.pageIndex = 0;
		this.search();
  }
  
  filterOnChange(filters: string) {
		this.selectedFilter = filters;
		this.pageIndex = 0;
		this.search();
  }
  
  onPageChange($event: any): void {
		this.pageIndex = $event.pageIndex;
		this.pageSize = $event.pageSize;
		this.search();
  }
  
  onSelectStudent(id: number): void {
    this.selectedStudentId = id;
    this.getFormValue.emit(this.selectedStudentId);
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

}
